import React, { useState, useEffect, useCallback } from "react"; // Added useCallback
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index";
import { IoMdArrowBack } from "react-icons/io";
import { 
  FiEdit, FiLogOut, FiHome, FiPlusSquare, 
  FiMail, FiUser, FiMapPin, FiPhone, FiCreditCard,
  FiAtSign, FiSettings, FiChevronRight, FiShield 
} from "react-icons/fi";

const Profile = ({ refreshAuth }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    profileImage: "https://res.cloudinary.com/dvuq6vmiy/image/upload/v1758705055/istockphoto-1288129985-612x612_hhrco1.jpg",
    phone: "",
    lga: "",
    address: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeofuser, settypeofuser] = useState("");

    const token = localStorage.getItem("token");
     

      const decoded = jwtDecode(token);
      const userId = decoded.id || decoded._id;

  // 1. Wrapped fetch logic in useCallback (Matches your mobile logic)
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

     
      if (!token) throw new Error("User not logged in");

      
      const id = decoded.id || decoded._id;
      
      if (!id) return;

      settypeofuser(decoded.typeofuser || "USER");

      const response = await api.UserProfile(id);

      setProfile({
        name: response.data?.Name || "N/A",
        email: response.data?.Email || "N/A",
        profileImage: response.data?.profileImage || "https://res.cloudinary.com/dvuq6vmiy/image/upload/v1758705055/istockphoto-1288129985-612x612_hhrco1.jpg",
        phone: response.data?.phone || "N/A",
        lga: response.data?.lga || "N/A",
        address: response.data?.address || "N/A",
      });
    } catch (err) {
      console.error("Fetch Error:", err);
      setError("⚠️ Failed to fetch profile.");
    } finally {
      setLoading(false);
    }
  }, []);

  // 2. Refresh profile on mount and when window gains focus (Web alternative to useFocusEffect)
  useEffect(() => {
    fetchProfile();
    window.addEventListener("focus", fetchProfile);
    return () => window.removeEventListener("focus", fetchProfile);
  }, [fetchProfile]);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to sign out?")) {
      localStorage.removeItem("token");
      if (refreshAuth) refreshAuth();
      navigate("/auth");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 font-bold animate-pulse">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-24">
      {/* 🔝 Header */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex justify-between items-center shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700"
        >
          <IoMdArrowBack size={24} />
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Account Settings</h1>
        <button 
          onClick={() => navigate("/settings")}
          className="p-2 text-gray-700 hover:bg-gray-100 rounded-full transition"
        >
          <FiSettings size={22} />
        </button>
      </div>

      <div className="max-w-2xl mx-auto w-full p-4 sm:p-6">
        {/* 👤 Identity Section */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative group">
            <div className="w-32 h-32 rounded-[2.5rem] bg-white p-1.5 shadow-xl border border-gray-100 overflow-hidden transition-transform duration-500 group-hover:scale-105">
              <img
                src={profile.profileImage}
                alt="Profile"
                className="object-cover w-full h-full rounded-[2.1rem]"
              />
            </div>
            <button 
                onClick={() => navigate("/edit-profile")}
                className="absolute -bottom-2 -right-2 bg-orange-600 text-white p-2.5 rounded-2xl shadow-lg border-4 border-gray-50 hover:bg-orange-700 transition-colors"
            >
              <FiEdit size={16} />
            </button>
          </div>
          <h2 className="mt-4 text-2xl font-black text-gray-900">{profile.name}</h2>
          <div className="flex items-center gap-1 bg-orange-50 px-3 py-1 rounded-full mt-2">
            <FiShield size={12} className="text-orange-600" />
            <p className="text-orange-600 font-black text-[10px] uppercase tracking-widest">
              {typeofuser} Account
            </p>
          </div>
        </div>

        {/* 📋 Details List */}
        <div className="space-y-4">
          {[
            { label: "Email Address", value: profile.email, icon: <FiAtSign className="text-blue-500" /> },
            { label: "Phone Number", value: profile.phone, icon: <FiPhone className="text-green-500" /> },
            { label: "LGA / Location", value: profile.lga, icon: <FiMapPin className="text-red-500" /> },
            { label: "Residential Address", value: profile.address, icon: <FiMapPin className="text-orange-500" /> },
          ].map((item, idx) => (
            <div key={idx} className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">
                {item.label}
              </label>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                  {item.icon}
                </div>
                <p className="text-gray-800 font-bold text-sm">
                  {item.value || "Not Set"}
                </p>
              </div>
            </div>
          ))}

          {/* ⚙️ App Preferences Card */}
          <button 
            onClick={() => navigate("/settings")}
            className="w-full text-left bg-white p-5 rounded-[2rem] border border-gray-200 shadow-sm hover:border-orange-200 transition-all group mt-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-500 group-hover:bg-orange-50 group-hover:text-orange-600 transition-colors">
                  <FiSettings size={22} />
                </div>
                <div>
                  <p className="text-gray-900 font-black text-sm">App Preferences</p>
                  <p className="text-[11px] font-bold text-gray-400">Security, Notifications & Privacy</p>
                </div>
              </div>
              <FiChevronRight size={20} className="text-gray-300 group-hover:text-orange-600 transition-transform group-hover:translate-x-1" />
            </div>
          </button>
        </div>

        {/* 🛠 Actions */}
        <div className="mt-10 flex flex-col gap-3">
            <button
              onClick={() => navigate("/edit-profile")}
              className="w-full py-4 bg-gray-900 text-white font-black rounded-2xl hover:bg-orange-600 shadow-lg shadow-gray-200 transition-all active:scale-[0.98]"
            >
              Update Profile Details
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full py-4 bg-white text-red-600 border border-red-100 font-black rounded-2xl hover:bg-red-50 transition-all flex items-center justify-center gap-2"
            >
              <FiLogOut /> Sign Out
            </button>
        </div>

        {error && (
          <p className="mt-6 text-center text-xs text-red-500 font-black bg-red-50 p-4 rounded-2xl border border-red-100">
            {error}
          </p>
        )}
      </div>

      {/* Modern Bottom Navbar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-t border-gray-100 md:flex justify-around items-center py-3 z-[100] hidden">
        <button onClick={() => navigate("/")} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600">
          <FiHome size={22} />
          <span className="text-[10px] font-bold">Home</span>
        </button>
        <button onClick={() => navigate("/sellproduct")} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600">
          <FiPlusSquare size={22} />
          <span className="text-[10px] font-bold">Post</span>
        </button>
          <button onClick={() => navigate(`/balancepage/${userId}`)} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600 transition-all">
                                    <FiCreditCard size={22} />
                                    <span className="text-[9px] font-black uppercase tracking-widest">Wallet</span>
                                  </button>
        <button onClick={() => navigate("/conversations")} className="flex flex-col items-center gap-1 text-gray-400 hover:text-orange-600">
          <FiMail size={22} />
          <span className="text-[10px] font-bold">Inbox</span>
        </button>
        <button 
          onClick={() => (typeofuser === "Customer" || typeofuser === "Rider") ? navigate("/profile") : navigate("/profile-seller")} 
          className="flex flex-col items-center gap-1 text-orange-600"
        >
          <FiUser size={22} />
          <span className="text-[10px] font-bold">Profile</span>
        </button>
      </nav>
    </div>
  );
};

export default Profile;